import type { ColDef, DefaultMenuItem, MenuItemDef, Theme, ValueGetterParams } from 'ag-grid-community';
import { MEMBER_SUBSET_OP_LABELS, MemberRow, MemberSubsetOp } from 'src/components/star-grid/utils/subset-editor/memberSubset';
import { expandedRowUids, NodeWalker, selectedRowUids, splitByExpansion } from 'src/components/star-grid/utils/subset-editor/memberSubsetEditor';

// Empty spans drawn as boxed +/- in CSS: the ⊞/⊟ glyphs rendered tiny and inconsistently across fonts.
export const SUBSET_GRID_ICONS = {
  groupExpanded: () => '<span class="sg-subset-toggle sg-subset-toggle-open"></span>',
  groupContracted: () => '<span class="sg-subset-toggle sg-subset-toggle-closed"></span>'
};

/** Compact, ruled and quiet: the subset editor people already use is a dense list, not a data grid. */
export const subsetTheme = (theme: Theme): Theme =>
  theme.withParams({
    // Inherit the dialog's font: naming one put two typefaces inside one modal.
    fontFamily: 'inherit',
    fontSize: 13,
    headerFontSize: 13,
    headerFontWeight: 400,
    cellHorizontalPadding: 4,
    iconSize: 13,
    // A member tree goes deep, so the default indent pushes deep labels off-screen.
    rowGroupIndentSize: 14,
    rowVerticalPaddingScale: 1.4,
    wrapperBorder: false,
    headerColumnBorder: false,
    rowBorder: false,
    // Literal colours matching the editor being replicated; the tokens were too light or the wrong hue.
    backgroundColor: '#f4f7fa',
    headerBackgroundColor: '#dfe5ec',
    headerTextColor: 'var(--color-text-body-default, #000716)',
    // No zebra striping or selection tint: indentation carries structure, and the checkbox shows selection.
    oddRowBackgroundColor: 'transparent',
    rowHoverColor: 'var(--color-background-dropdown-item-hover, #f4f4f4)',
    selectedRowBackgroundColor: 'transparent',
    // The context menu is portaled, so only theme params (not nested CSS) can style it.
    menuTextColor: 'var(--color-text-body-default, #000716)',
    menuBackgroundColor: 'var(--color-background-container-content, #ffffff)',
    menuBorder: { color: 'var(--color-border-divider-default, #d1d5db)', width: 1, style: 'solid' },
    menuSeparatorColor: 'var(--color-border-divider-default, #e9ebed)',
    menuShadow: { offsetX: 0, offsetY: 2, radius: 8, spread: 0, color: 'rgba(0, 7, 22, 0.18)' },
    borderRadius: 4
  });

/** Weight only: the edge into this position, so blank (null) is distinct from a genuine 0. */
export const subsetColumnDefs = (): ColDef<MemberRow>[] => [
  {
    headerName: 'Weight',
    field: 'weight',
    width: 90,
    sortable: false,
    // Not `type: 'rightAligned'`: that also flips the header and parks its menu button on the left edge.
    headerClass: 'sg-subset-centred-header',
    cellClass: 'sg-subset-weight',
    valueFormatter: ({ value }: { value?: number | null }) => (value === null || value === undefined ? '' : String(value))
  }
];

export const subsetAutoGroupColumnDef = (): ColDef<MemberRow> => ({
  headerName: 'Name',
  flex: 1,
  minWidth: 240,
  // Capped, or `flex: 1` parks Weight against the right edge of a wide modal, far from the names.
  maxWidth: 560,
  headerClass: 'sg-subset-centred-header',
  // The tree path is row uids, so the label has to come off the row rather than the group key.
  valueGetter: (params: ValueGetterParams<MemberRow>) => params.data?.label ?? '',
  cellRendererParams: { suppressCount: true },
  cellClass: (params: { data?: MemberRow }) => (params.data?.isLeaf ? 'sg-subset-leaf' : 'sg-subset-node')
});

/** Keep variants, widest scope last. */
const MENU_OPS: readonly MemberSubsetOp[] = ['keep', 'keepChildren', 'keepDescendants', 'keepLeaves', 'leavesOnly'];

// HTML strings: AG Grid menu icons take markup (unlike cellRenderer, which escapes it).
const MENU_ICONS: Readonly<Record<MemberSubsetOp, string>> = {
  keep: '<span class="sg-subset-menu-icon sg-subset-menu-keep">\u2713</span>',
  keepChildren: '<span class="sg-subset-menu-icon sg-subset-menu-keep">\u2713</span>',
  keepDescendants: '<span class="sg-subset-menu-icon sg-subset-menu-keep">\u2713</span>',
  keepLeaves: '<span class="sg-subset-menu-icon sg-subset-menu-keep">\u2713</span>',
  leavesOnly: '<span class="sg-subset-menu-icon sg-subset-menu-leaf">\u2740</span>'
};
const REMOVE_ICON = '<span class="sg-subset-menu-icon sg-subset-menu-remove">\u2715</span>';
const PARENT_ICON = '<span class="sg-subset-menu-icon sg-subset-menu-parent">\u21B0</span>';

export interface SubsetContextMenuHandlers {
  readonly onRunOp: (op: MemberSubsetOp, selected: readonly string[]) => void;
  readonly onRemove: (uids: readonly string[], withDescendants: boolean) => void;
  readonly onAddParents: (clickedKey: string) => void;
}

/**
 * The right-click menu. Acts on the ticked rows when the clicked row is one of them, else on the clicked
 * row alone — the file-manager rule.
 *
 * Keep addresses POSITIONS; Remove names ROWS, read from the grid's own selection, because `Parent` and
 * drill can put one position on screen twice. Parent always acts on the clicked row: a ticked rollup
 * cascades to its subtree, and Parent is a statement about one member.
 */
export const buildSubsetContextMenu = (
  clicked: MemberRow | undefined,
  checked: ReadonlySet<string>,
  api: NodeWalker,
  { onRunOp, onRemove, onAddParents }: SubsetContextMenuHandlers
): (MenuItemDef | DefaultMenuItem)[] => {
  if (!clicked) return [];
  const clickedIsTicked = checked.has(clicked.key);
  const selected = clickedIsTicked ? [...checked] : [clicked.key];
  const selectedUids = clickedIsTicked ? selectedRowUids(api) : [];
  if (selectedUids.length === 0) selectedUids.push(clicked.uid);
  return [
    'copy',
    'separator',
    ...MENU_OPS.map((op) => ({
      name: MEMBER_SUBSET_OP_LABELS[op],
      icon: MENU_ICONS[op],
      action: () => onRunOp(op, selected)
    })),
    'separator',
    {
      name: 'Remove Elements',
      icon: REMOVE_ICON,
      // A collapsed row takes its hidden descendants with it; an expanded one goes alone and its children lift a level.
      action: () => {
        const { collapsed, opened } = splitByExpansion(selectedUids, expandedRowUids(api));
        if (collapsed.length > 0) onRemove(collapsed, true);
        if (opened.length > 0) onRemove(opened, false);
      }
    },
    {
      name: 'Remove Elements + Descendants',
      icon: REMOVE_ICON,
      action: () => onRemove(selectedUids, true)
    },
    'separator',
    {
      name: 'Parent',
      icon: PARENT_ICON,
      action: () => onAddParents(clicked.key)
    }
  ];
};
